// Sentry must be first.
import * as Sentry from "@sentry/react";

import { setupListeners } from "@reduxjs/toolkit/query";
import escapeRegExp from "lodash-es/escapeRegExp.js";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import App from "./app/index.js";
import { createStore } from "./app/store.js";
import { renderErrorFallback } from "./helpers/errorFallback.js";
import { detectIsEmbeddedInFountain } from "./helpers/fountain.js";
import "./index.css";

// TODO: Use PostHog <> Sentry integration for error reporting.
Sentry.init({
  // Unset this environment variable in development unless you're testing Sentry
  dsn: import.meta.env.VITE_SENTRY_ADMIN_DSN,
  // TODO: Set up component tracing and Redux integrations.
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  environment: import.meta.env.PROD ? "production" : "development",
  // Trace 5% of sessions. The api inherits this decision for the requests
  // a session makes, so it sets the api's sampling for this app's traffic too.
  tracesSampleRate: 0.05,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    /^\//,
    new RegExp(`^${escapeRegExp(import.meta.env.VITE_API_BASE_URL)}`, "i"),
  ],
});

// Must run before the first render: components read the result during render.
detectIsEmbeddedInFountain();

const store = createStore();

const el = document.getElementById("root");
if (el) {
  const root = createRoot(el);

  const app = (
    <Sentry.ErrorBoundary fallback={renderErrorFallback}>
      <Provider store={store}>
        <App />
      </Provider>
    </Sentry.ErrorBoundary>
  );

  root.render(
    import.meta.env.PROD ? app : <React.StrictMode>{app}</React.StrictMode>,
  );

  setupListeners(store.dispatch);
} else {
  throw new Error("Could not find root element");
}
